import { CommonModule, Location } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MEMBERSHIP_GEO_COUNTRY_API_SERVICE_TOKEN } from '@nk/api-lib';
import { SearchQueryMethod, SimpleSearchWithDistanceEntity } from '@nk/data-entities-lib';
import { ButtonDirective, CardComponent, CardContentComponent, HighlightTextPipe, TitleCasePipe } from '@nk/ui-lib';
import { combineLatest } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { CompanyPersonSearchService } from '../../services/company-person-search.service';

@Component({
  selector: 'nk-search-view',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ButtonDirective,
    CardComponent,
    CardContentComponent,
    HighlightTextPipe,
    TitleCasePipe,
  ],
  templateUrl: './search-view.component.html',
})
export class SearchViewComponent implements OnInit {
  public searchQuery = '';
  public searchList: SimpleSearchWithDistanceEntity[] = [];
  public loading = false;
  public firstSearch = true;
  public includeDeadCompanies = true;
  public searchQuerySent: string[] | undefined;
  private _destroy = inject(DestroyRef);
  private _searchService = inject(CompanyPersonSearchService);
  private _membershipGeoCountryService = inject(MEMBERSHIP_GEO_COUNTRY_API_SERVICE_TOKEN);
  private _activeRoute = inject(ActivatedRoute);
  private _router = inject(Router);
  private _location = inject(Location);

  public ngOnInit(): void {
    combineLatest([this._activeRoute.params, this._activeRoute.queryParams])
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe((paramsPair) => {
        const [params, queryParams] = paramsPair;
        this.searchQuery = params['searchQuery'];
        const feelingLuck = queryParams['lucky'] === 'true';
        this.doSearch(feelingLuck);
      });
  }

  public doSearch(feelingLuck = false): void {
    if (this.searchQuery && this.searchQuery.length > 0) {
      this.firstSearch = false;
      this.searchList = [];
      this.loading = true;
      this._location.replaceState('/search/' + this.searchQuery);
      const limit = feelingLuck ? 1 : 100;

      this._membershipGeoCountryService.appMembershipGeoCountryPreference$
        .pipe(
          mergeMap((geoCountries) => {
            const geoCountryIdentifiers = geoCountries.map((c) => c.country_code_two);
            return this._searchService.search(
              this.searchQuery,
              limit,
              SearchQueryMethod.fuzzy,
              this.includeDeadCompanies,
              geoCountryIdentifiers,
            );
          }),
          takeUntilDestroyed(this._destroy),
        )
        .subscribe({
          next: (payload) => {
            let redirect = false;
            if (feelingLuck && payload.length > 0) {
              const entity = payload[0];
              if (entity.company_uuid) {
                this.redirectToCompany(entity.company_uuid);
                redirect = true;
              } else if (entity.person_uuid) {
                this.redirectToPerson(entity.person_uuid);
                redirect = true;
              }
            }
            if (!redirect) {
              this.searchQuerySent = [this.searchQuery];
              this.loading = false;
              this.searchList = payload;
            }
          },
          error: (error) => {
            console.error('error', error);
          },
        });
    }
  }

  public redirectToPerson(personUuid: string): void {
    void this._router.navigate([`/person/${personUuid}`]);
  }

  public redirectToCompany(companyUuid: string): void {
    void this._router.navigate([`/company/${companyUuid}`]);
  }
}
